import React, { useState, useEffect, useCallback } from 'react';
import { Download, X, Share, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const DISMISS_KEY = 'az_count_install_dismissed';
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

function isIOSSafari() {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream && /Safari/.test(ua);
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    // Already installed as standalone — never show
    if (isStandalone()) return;

    // Check if dismissed recently
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed && Date.now() - parseInt(dismissed, 10) < DISMISS_DURATION) return;

    // iOS Safari — show manual instructions
    if (isIOSSafari()) {
      setShowIOSGuide(true);
      return;
    }

    // Android/Chrome/Edge — intercept beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // If app gets installed, hide everything
    const installed = () => {
      setShowBanner(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('appinstalled', installed);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installed);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
    setInstalling(false);
  }, [deferredPrompt]);

  const handleDismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShowBanner(false);
    setShowIOSGuide(false);
  }, []);

  // Android/Chrome install banner
  if (showBanner) {
    return (
      <div
        className="mx-4 mt-3 mb-1 bg-cyan-50 border border-cyan-200 rounded-xl px-4 py-3 flex items-center gap-3 animate-in slide-in-from-top duration-300"
        data-testid="install-prompt-banner"
      >
        <div className="p-2 bg-cyan-100 rounded-lg shrink-0">
          <Download className="w-5 h-5 text-cyan-700" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-cyan-900">Install AbbeyZone Count</p>
          <p className="text-xs text-cyan-700">Add to your home screen for faster access</p>
        </div>
        <Button
          size="sm"
          onClick={handleInstall}
          disabled={installing}
          className="shrink-0 bg-cyan-700 hover:bg-cyan-800 text-white h-9 px-4"
          data-testid="install-prompt-install-btn"
        >
          Install
        </Button>
        <button
          onClick={handleDismiss}
          className="p-1.5 text-cyan-400 hover:text-cyan-600 shrink-0"
          data-testid="install-prompt-dismiss-btn"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // iOS Safari manual instructions
  if (showIOSGuide) {
    return (
      <div
        className="mx-4 mt-3 mb-1 bg-cyan-50 border border-cyan-200 rounded-xl px-4 py-3 animate-in slide-in-from-top duration-300"
        data-testid="install-prompt-ios"
      >
        <div className="flex items-start gap-3">
          <div className="p-2 bg-cyan-100 rounded-lg shrink-0 mt-0.5">
            <Download className="w-5 h-5 text-cyan-700" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-cyan-900">Install AbbeyZone Count</p>
            <div className="mt-2 space-y-1.5">
              <div className="flex items-center gap-2 text-xs text-cyan-800">
                <span className="w-5 h-5 rounded-full bg-cyan-200 text-cyan-800 flex items-center justify-center font-bold shrink-0">1</span>
                <span>Tap the <Share className="w-3.5 h-3.5 inline -mt-0.5" /> Share button in Safari</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-cyan-800">
                <span className="w-5 h-5 rounded-full bg-cyan-200 text-cyan-800 flex items-center justify-center font-bold shrink-0">2</span>
                <span>Scroll down and tap "Add to Home Screen"</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-cyan-800">
                <span className="w-5 h-5 rounded-full bg-cyan-200 text-cyan-800 flex items-center justify-center font-bold shrink-0">3</span>
                <span>Tap "Add" to confirm</span>
              </div>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="p-1.5 text-cyan-400 hover:text-cyan-600 shrink-0"
            data-testid="install-prompt-ios-dismiss-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
